import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, EyeOff } from 'lucide-react';
import { formatCents } from './_money';

/*
 * InvoiceDetail — one invoice: its line items, the payments and refunds against
 * it, and the balance (Card P150).
 *
 * ⛔ THE LAST LEVEL, and the only place a line item is shown. The server
 * records the PHI subject audit for this read, which is what makes naming the
 * bill-to defensible here and nowhere higher.
 *
 * ⚠ Integer cents throughout, through the shared formatter. The 28 Oct bug was
 * a cents value handed to a formatter that assumed dollars.
 */

const date = (v) => (v ? new Date(v).toLocaleDateString() : '—');

export default function InvoiceDetail({ invoiceId, onClose, onOpenContact }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.getFinanceInvoice !== 'function') {
        throw new Error('Invoice detail is unavailable in this context.');
      }
      const res = await api.getFinanceInvoice(invoiceId);
      // ⚠ A refusal is NOT an empty invoice. Surfacing the server's message
      // keeps "you may not see this" distinct from "there is nothing here".
      if (res && res.error) throw new Error(res.message || 'That invoice could not be opened.');
      setState({ loading: false, error: null, data: res });
    } catch (e) {
      setState({ loading: false, error: e.message || 'That invoice could not be opened.', data: null });
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const { loading, error, data } = state;

  if (loading) return <div data-testid="invoice-loading" style={S.muted}>Loading invoice…</div>;
  if (error) {
    return (
      <div data-testid="invoice-error" style={S.error}>
        <AlertTriangle size={15} /><span style={{ marginLeft: 8 }}>{error}</span>
      </div>
    );
  }
  if (!data || !data.invoice) return null;

  const inv = data.invoice;
  const items = Array.isArray(data.items) ? data.items : [];
  const payments = Array.isArray(data.payments) ? data.payments : [];
  const refunds = Array.isArray(data.refunds) ? data.refunds : [];

  return (
    <section data-testid="invoice-detail" style={S.wrap}>
      <header style={S.head}>
        <div>
          <h3 style={S.title}>Invoice {inv.invoice_number || inv.ghl_invoice_id}</h3>
          <div style={S.sub} data-testid="invoice-bill-to">
            {/*
              * ⚠ The server prefers the CRM contact's name over the invoice's
              * encrypted snapshot, and a snapshot that will not decrypt arrives
              * as "[unreadable]" rather than as a sentinel string masquerading
              * as a name. Nothing is decrypted here.
              */}
            {inv.bill_to || '(no bill-to recorded)'}
            {inv.bill_to_kind === 'organization' && ' · organization'}
            {inv.bill_to_kind === 'unmatched' && ' · not matched to a contact'}
          </div>
        </div>
        <button type="button" onClick={onClose} style={S.close} data-testid="invoice-close">Close</button>
      </header>

      <div style={S.figures}>
        <div style={S.fig}><div style={S.figLabel}>Total</div>
          <div style={S.figValue} data-testid="invoice-total">{formatCents(inv.total_cents)}</div></div>
        <div style={S.fig}><div style={S.figLabel}>Paid</div>
          <div style={S.figValue} data-testid="invoice-paid">{formatCents(inv.paid_cents)}</div></div>
        <div style={S.fig}><div style={S.figLabel}>Balance</div>
          <div style={{ ...S.figValue, color: Number(inv.balance_cents) > 0 ? '#9B2C2C' : '#1B6E3C' }}
            data-testid="invoice-balance">{formatCents(inv.balance_cents)}</div></div>
        <div style={S.fig}><div style={S.figLabel}>Issued / Due</div>
          <div style={S.figSmall}>{date(inv.issued_at)} → {date(inv.due_at)}</div></div>
      </div>

      {inv.contact_id != null && onOpenContact && (
        <button type="button" style={S.link} data-testid="invoice-open-contact"
          onClick={() => onOpenContact(inv.contact_id)}>
          Open this contact in the CRM →
        </button>
      )}

      <div style={S.subhead}>Line items</div>
      {items.length === 0
        ? <div style={S.muted} data-testid="invoice-no-items">No line items were mirrored for this invoice.</div>
        : (
          <table style={S.table} data-testid="invoice-items">
            <thead><tr><th style={S.th}>Qty</th><th style={S.th}>Unit</th><th style={{ ...S.th, textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={S.td}>{it.quantity}</td>
                  <td style={S.td}>{formatCents(it.unit_price_cents)}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{formatCents(it.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      <div style={S.subhead}>Payments</div>
      {payments.length === 0
        ? <div style={S.muted} data-testid="invoice-no-payments">No payments are recorded against this invoice.</div>
        : (
          <table style={S.table} data-testid="invoice-payments">
            <thead><tr><th style={S.th}>Date</th><th style={S.th}>Provider</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td style={S.td}>{date(p.transacted_at)}</td>
                  <td style={S.td}>{p.provider || '—'}</td>
                  <td style={S.td}>{p.status || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{formatCents(p.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      <div style={S.subhead}>Refunds</div>
      {/*
        * ⛔⛔ THREE STATES, NOT TWO. `ghl_payment_refunds` has never been
        * written — migration 197 created it and nothing populates it — so an
        * empty list means "refund records are not mirrored", NOT "there were no
        * refunds". Rendering a bare "none" would be a false zero on the one
        * figure a reader would act on.
        */}
      {data.refundsTracked === false
        ? (
          <div style={S.notTracked} data-testid="invoice-refunds-not-tracked">
            <EyeOff size={13} />
            <span style={{ marginLeft: 6 }}>
              Refund records are not mirrored yet, so this is blank whether or not a refund exists.
              A refunded payment still shows above with status <strong>refunded</strong>.
            </span>
          </div>
        )
        : refunds.length === 0
          ? <div style={S.muted} data-testid="invoice-no-refunds">No refunds against this invoice.</div>
          : (
            <table style={S.table} data-testid="invoice-refunds">
              <thead><tr><th style={S.th}>Date</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>
                {refunds.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>{date(r.refunded_at)}</td>
                    <td style={S.td}>{r.status || '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{formatCents(r.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </section>
  );
}

const S = {
  wrap: { border: '1px solid #DEE2E6', borderRadius: 12, padding: 16, marginTop: 16, background: '#fff' },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' },
  sub: { fontSize: 12, color: '#6C757D', marginTop: 2 },
  close: { background: 'none', border: '1px solid #CED4DA', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  figures: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  fig: { flex: '1 1 130px', border: '1px solid #F1F3F5', borderRadius: 9, padding: '9px 11px' },
  figLabel: { fontSize: 11, fontWeight: 700, color: '#495057' },
  figValue: { fontSize: 17, fontWeight: 700, marginTop: 2, color: '#212529' },
  figSmall: { fontSize: 12, marginTop: 4, color: '#495057' },
  link: { background: 'none', border: 'none', color: '#1864AB', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 600, padding: 0, marginBottom: 6 },
  subhead: { marginTop: 14, marginBottom: 5, fontSize: 12, fontWeight: 700, color: '#495057' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 9px', borderBottom: '2px solid #DEE2E6', color: '#495057', fontWeight: 700, fontSize: 11 },
  td: { padding: '7px 9px', borderBottom: '1px solid #F1F3F5', color: '#343A40' },
  muted: { fontSize: 12, color: '#868E96', padding: '5px 2px' },
  notTracked: { display: 'flex', alignItems: 'center', fontSize: 11, color: '#6C757D', background: '#F8F9FA', padding: '8px 10px', borderRadius: 7 },
  error: { display: 'flex', alignItems: 'center', padding: 11, borderRadius: 9, background: '#FDECEA', color: '#7B241C', fontSize: 13, marginTop: 16 },
};
